import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Suppliers = { event: { id: number; name: string }; suppliers: string[] };
type CatalogueLine = { code: string; name: string; count: number };

/**
 * Écran d'export (plan §5.12). Les téléchargements sont de simples liens vers
 * des routes qui renvoient un CSV en pièce jointe : le navigateur télécharge,
 * rien à gérer côté JS, et la session cookie authentifie la requête.
 */
export default function Export() {
  const { data, isPending } = useQuery<Suppliers>({
    queryKey: ['admin', 'export', 'suppliers'],
    queryFn: () => api('/api/admin/export/suppliers'),
  });
  const catalogue = useQuery<{ lines: CatalogueLine[] }>({
    queryKey: ['admin', 'export', 'catalogue-lines'],
    queryFn: () => api('/api/admin/export/catalogue-lines'),
  });

  // Intervalle de dates optionnel (le show dure plusieurs jours) : ne sortir que
  // les commandes soumises entre « du » et « au » inclusivement.
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const plage = (base: string) => {
    const params = new URLSearchParams();
    if (du) params.set('from', du);
    if (au) params.set('to', au);
    const qs = params.toString();
    return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
  };

  return (
    <div className="space-y-6">
      <Section titre="Commandes par client">
        <p className="text-muted-foreground mb-4 text-sm">
          Rapport unique des commandes soumises de {isPending ? '…' : data?.event.name}, regroupées
          par client (coordonnées + lignes de commande). Les <b>nouveaux clients (walk-in) sont en
          premier</b>, et une <b>ligne vide sépare chaque client</b> — idéal pour recréer comptes et
          commandes dans l'ERP. Fichier <b>Excel (.xlsx)</b>.
        </p>
        <div className="border-input mb-4 flex flex-wrap items-end gap-4 rounded-lg border p-3">
          <div className="flex flex-col">
            <label htmlFor="export-du" className="text-muted-foreground mb-1 text-sm font-medium">
              Du (optionnel)
            </label>
            <input
              id="export-du"
              type="date"
              value={du}
              max={au || undefined}
              onChange={(e) => setDu(e.target.value)}
              className="border-input rounded-md border px-2 py-1"
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="export-au" className="text-muted-foreground mb-1 text-sm font-medium">
              Au (optionnel)
            </label>
            <input
              id="export-au"
              type="date"
              value={au}
              min={du || undefined}
              onChange={(e) => setAu(e.target.value)}
              className="border-input rounded-md border px-2 py-1"
            />
          </div>
          {(du || au) && (
            <button
              type="button"
              onClick={() => {
                setDu('');
                setAu('');
              }}
              className="text-primary text-sm font-medium underline"
            >
              Effacer les dates
            </button>
          )}
          <p className="text-muted-foreground w-full text-sm">
            Laisser vide = toutes les commandes du show. Sinon, seules les commandes soumises entre
            ces dates (incluses, fuseau local) sont exportées.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href={plage('/api/admin/export/orders-by-customer.xlsx')}
            titre="Commandes par client"
            sous="clients + lignes, nouveaux d'abord"
          />
          <LienExport
            href={plage('/api/admin/export/orders-express.xlsx?clients=existing')}
            titre="Express — clients existants"
            sous="déjà dans la base, à re-commander"
          />
          <LienExport
            href={plage('/api/admin/export/orders-express.xlsx?clients=new')}
            titre="Express — nouveaux clients"
            sous="coordonnées + commandes, à créer d'abord"
          />
        </div>
        <p className="text-muted-foreground mt-3 text-sm">
          <b>Version express</b> : par article, le <b>code super</b>{' '}
          (<span className="font-mono">code_ligne-no_piece</span>), la quantité, le prix unitaire et
          le total, regroupé <b>par client</b> avec une ligne colorée{' '}
          <b>GRAND TOTAL CLIENT</b> (toutes ses commandes) à la fin de chaque client. Deux fichiers :
          les <b>clients existants</b>{' '}
          (n° client seul, à re-commander directement) et les <b>nouveaux clients</b> (walk-in), qui
          portent en plus <b>nom, adresse, ville, code postal, téléphone et courriel</b> pour les
          créer dans le système avant de passer leurs commandes.
        </p>
      </Section>

      <Section titre="Cadeaux">
        <p className="text-muted-foreground mb-4 text-sm">
          Cadeaux remis aux clients : <b>une ligne par remise</b> (un même cadeau peut être remis
          plusieurs fois) avec son <b>seuil</b>, et une ligne <b>TOTAL CLIENT</b> par client (nombre
          de cadeaux + total des seuils). Fichier <b>Excel (.xlsx)</b>.
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href="/api/admin/export/claims.xlsx"
            titre="Prix réclamés"
            sous="une ligne par remise + total par client"
          />
          <LienExport
            href="/api/admin/export/unclaimed-gifts.xlsx"
            titre="Cadeaux non réclamés"
            sous="clients dont le budget restant couvre encore un cadeau"
          />
        </div>
      </Section>

      <Section titre="Coupons de tirage">
        <p className="text-muted-foreground mb-4 text-sm">
          Comptabilisation des coupons de participation <b>distribués</b>, par ligne (fournisseur)
          avec sa tranche, plus un <b>TOTAL</b>. Fichier <b>Excel (.xlsx)</b>.
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href="/api/admin/export/coupons.xlsx"
            titre="Coupons distribués"
            sous="par ligne + total"
          />
        </div>
      </Section>

      <Section titre="Étiquettes imprimées">
        <p className="text-muted-foreground mb-4 text-sm">
          Détail des étiquettes imprimées au comptoir d'accueil (le décompte des visiteurs). Une{' '}
          <b>ligne par étiquette</b> — une même étiquette réimprimée compte pour autant de
          visiteurs. Fichier <b>Excel (.xlsx)</b>.
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href="/api/admin/export/label-prints.xlsx"
            titre="Étiquettes imprimées"
            sous="visiteurs — une ligne par étiquette"
          />
        </div>
      </Section>

      <Section titre="Configuration">
        <p className="text-muted-foreground mb-4 text-sm">
          Sauvegarde des pages de configuration, réimportable dans <b>Imports</b>. <b>Lignes</b> (code,
          fournisseur, coupon/tranche), <b>Cadeaux</b> (cadeau, seuil, fournisseur) et <b>Rôles</b>{' '}
          (représentants, fournisseurs assignés). Fichiers <b>Excel (.xlsx)</b>.{' '}
          <b>Aucun mot de passe n'est exporté.</b>
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href="/api/admin/config/lines.xlsx"
            titre="Lignes"
            sous="codes + fournisseurs + tranches"
          />
          <LienExport
            href="/api/admin/config/gifts.xlsx"
            titre="Cadeaux"
            sous="cadeaux + seuils par fournisseur"
          />
          <LienExport
            href="/api/admin/config/reps.xlsx"
            titre="Rôles"
            sous="représentants + fournisseurs (sans mot de passe)"
          />
        </div>
      </Section>

      <Section titre="Anomalies du catalogue">
        <p className="text-muted-foreground mb-4 text-sm">
          Contrôle qualité : liste des <b>produits actifs</b> présentant une anomalie — prix liste ou
          prix show <b>manquant ou à 0,01 $</b>, <b>description / n° de pièce / ligne manquants</b>,
          ou <b>description identique au n° de pièce</b>. Une colonne <span className="font-mono">anomalies</span>{' '}
          détaille chaque problème. Fichier <b>Excel (.xlsx)</b>.
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport
            href="/api/admin/export/catalogue-anomalies.xlsx"
            titre="Anomalies du catalogue"
            sous="produits à corriger, avec le détail"
          />
        </div>
      </Section>

      <Section titre="Catalogue par ligne">
        <p className="text-muted-foreground mb-4 text-sm">
          Les produits <b>d'une ligne à la fois</b>, en <b>Excel (.xlsx)</b> : colonnes{' '}
          <span className="font-mono">ligne</span>, <span className="font-mono">no_piece</span>,{' '}
          <span className="font-mono">description</span>, <span className="font-mono">prix_liste</span>{' '}
          et <span className="font-mono">prix_show</span> (produits actifs du catalogue).
        </p>

        {catalogue.isPending ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : (catalogue.data?.lines.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">Aucune ligne au catalogue.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalogue.data!.lines.map((l) => (
              <a
                key={l.code}
                download
                href={`/api/admin/export/catalogue/${encodeURIComponent(l.code)}.xlsx`}
                className="border-input hover:border-primary/50 flex flex-col rounded-lg border p-4"
              >
                <span className="font-semibold">{l.code}</span>
                <span className="text-muted-foreground text-sm">{l.name}</span>
                <span className="text-muted-foreground mt-2 text-xs">
                  {l.count} produit{l.count > 1 ? 's' : ''} · Télécharger (Excel) ↓
                </span>
              </a>
            ))}
          </div>
        )}
      </Section>

      <Section titre="PO par fournisseur">
        <p className="text-muted-foreground mb-4 text-sm">
          CSV pour créer les PO (colonnes : magasin, ligne, pièce, quantité — sans en-tête).
          <br />
          <b>Global</b> = toute la commande étiquetée magasin 1. <b>Mag. 1–4</b> = la commande du
          magasin choisi (PAS St-Hubert = 1, St-Jean = 2, Châteauguay = 3, Longueuil = 4 ; le reste
          va au 1).
        </p>

        {isPending ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : data && data.suppliers.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {data.suppliers.map((code) => (
              <div key={code} className="border-input rounded-lg border p-4">
                <div className="mb-2 font-semibold">{code}</div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <a
                    download
                    href={`/api/admin/export/supplier/${code}.csv?store=global`}
                    className="border-primary/40 text-primary hover:bg-primary/10 rounded-lg border px-3 py-1.5 font-medium"
                  >
                    Global ↓
                  </a>
                  {['1', '2', '3', '4'].map((s) => (
                    <a
                      key={s}
                      download
                      href={`/api/admin/export/supplier/${code}.csv?store=${s}`}
                      className="border-input hover:border-primary/50 rounded-lg border px-3 py-1.5 font-medium"
                    >
                      Mag. {s} ↓
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">Aucune vente à exporter pour l'instant.</p>
        )}
      </Section>
    </div>
  );
}

/** Section repliable de la page Export (repliée/déroulée d'un clic sur le titre). */
function Section({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <details open className="bg-card group rounded-xl border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-6 pt-5 pb-2 [&::-webkit-details-marker]:hidden">
        <h2 className="text-lg font-semibold">{titre}</h2>
        <span className="text-muted-foreground transition-transform group-open:rotate-180">▾</span>
      </summary>
      <div className="px-6 pb-6">{children}</div>
    </details>
  );
}

function LienExport({ href, titre, sous }: { href: string; titre: string; sous: string }) {
  return (
    <a
      href={href}
      // download laisse le serveur nommer le fichier via Content-Disposition.
      download
      className="border-input hover:border-primary/50 flex min-w-44 flex-col rounded-lg border p-4"
    >
      <span className="font-semibold">{titre}</span>
      <span className="text-muted-foreground text-sm">{sous}</span>
      <span className="text-primary mt-2 text-sm font-medium">Télécharger (Excel) ↓</span>
    </a>
  );
}
